// Smart Sash Admin Panel - RFID Authentication Context
// Manages RFID authentication and gRPC event streaming

import { RFIDAuthRequest } from '@/models';
import { rfidAuthService } from '@/services/api';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { tokenManager } from './tokenManager';

interface RFIDAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  authenticate: (sessionId: string, login: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const RFIDAuthContext = createContext<RFIDAuthContextType | undefined>(undefined);

interface RFIDAuthProviderProps {
  children: ReactNode;
}

/**
 * RFID Authentication Provider Component
 */
export const RFIDAuthProvider: React.FC<RFIDAuthProviderProps> = ({ children }) => {
  const { syncAuthState } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  /**
   * Authenticate user for RFID registration.
   * The server sets HttpOnly cookies on success.
   */
  const authenticate = async (
    sessionId: string,
    login: string,
    password: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const request: RFIDAuthRequest = {
        sessionId,
        login,
        password,
      };

      const { requiresPasswordChange } = await rfidAuthService.authenticate(request);

      tokenManager.setPasswordChangeRequired(requiresPasswordChange);
      await syncAuthState();

      if (requiresPasswordChange) {
        setSessionId(null);
        setIsAuthenticated(false);
        return true;
      }

      setSessionId(sessionId);
      setIsAuthenticated(true);
      return false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      console.error('RFID authentication error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout and cleanup.
   * HttpOnly auth cookies are cleared server-side on logout.
   */
  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      setIsAuthenticated(false);
      setSessionId(null);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const value: RFIDAuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    sessionId,
    authenticate,
    logout,
  };

  return <RFIDAuthContext.Provider value={value}>{children}</RFIDAuthContext.Provider>;
};

/**
 * Custom hook to use RFID auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useRFIDAuth = (): RFIDAuthContextType => {
  const context = useContext(RFIDAuthContext);
  if (context === undefined) {
    throw new Error('useRFIDAuth must be used within an RFIDAuthProvider');
  }
  return context;
};
